import { parseISO, format } from "date-fns";
import { useState } from "react";

import { DaySchedule } from "0.Mine/3.Interfaces/Schedule";

export type DayScheduleWithPeriod = DaySchedule & {
  openPeriod: "both" | "morning" | "afternoon";
};

export type ScheduleState = Record<string, DayScheduleWithPeriod>;

interface useBusinessScheduleLogicOnboardingProps {
  setScheduleToParent: (data: ScheduleState) => void;
}

export const defaultSchedule: ScheduleState = {
  monday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  tuesday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  wednesday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  thursday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  friday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  saturday: {
    isOpen: true,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
  sunday: {
    isOpen: false,
    isContinuous: false,
    morningOpen: "09:00",
    morningClose: "11:30",
    afternoonOpen: "14:30",
    afternoonClose: "19:00",
    openPeriod: "both",
  },
};

export const useBusinessScheduleLogicOnboarding = ({
  setScheduleToParent,
}: useBusinessScheduleLogicOnboardingProps) => {
  const [vacationStart, setVacationStart] = useState<Date | null>(null);
  const [vacationEnd, setVacationEnd] = useState<Date | null>(null);
  const [schedule, setSchedule] = useState<ScheduleState>(defaultSchedule);

  // Convert time strings to minutes for easier comparison
  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const handleDayToggle = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen },
    }));
    setScheduleToParent(schedule);
  };

  const handleContinuousToggle = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], isContinuous: !prev[day].isContinuous },
    }));
    setScheduleToParent(schedule);
  };

  const handleTimeChange = (day: string, period: string, value: string) => {
    const currentSchedule = schedule[day];
    let isValid = true;

    const valueInMinutes = timeToMinutes(value);

    switch (period) {
      case "morningOpen":
        if (valueInMinutes < 0 || valueInMinutes > timeToMinutes("12:59")) {
          alert("Morning opening time must be between 00:00 and 12:59");
          isValid = false;
        } else if (
          currentSchedule.morningClose &&
          valueInMinutes >= timeToMinutes(currentSchedule.morningClose)
        ) {
          alert("Opening time must be earlier than closing time");
          isValid = false;
        }
        break;
      case "morningClose":
        if (valueInMinutes < 0 || valueInMinutes > timeToMinutes("12:59")) {
          alert("Morning closing time must be between 00:00 and 12:59");
          isValid = false;
        } else if (
          currentSchedule.morningOpen &&
          valueInMinutes <= timeToMinutes(currentSchedule.morningOpen)
        ) {
          alert("Closing time must be later than opening time");
          isValid = false;
        }
        break;
      case "afternoonOpen":
        if (valueInMinutes < timeToMinutes("13:00") || valueInMinutes > timeToMinutes("23:59")) {
          alert("Afternoon opening time must be between 13:00 and 23:59");
          isValid = false;
        } else if (
          currentSchedule.afternoonClose &&
          valueInMinutes >= timeToMinutes(currentSchedule.afternoonClose)
        ) {
          alert("Opening time must be earlier than closing time");
          isValid = false;
        }
        break;
      case "afternoonClose":
        if (valueInMinutes < timeToMinutes("13:00") || valueInMinutes > timeToMinutes("23:59")) {
          alert("Afternoon closing time must be between 13:00 and 23:59");
          isValid = false;
        } else if (
          currentSchedule.afternoonOpen &&
          valueInMinutes <= timeToMinutes(currentSchedule.afternoonOpen)
        ) {
          alert("Closing time must be later than opening time");
          isValid = false;
        }
        break;
    }

    if (isValid) {
      setSchedule((prev) => ({
        ...prev,
        [day]: { ...prev[day], [period]: value },
      }));
      setScheduleToParent(schedule);
    }
  };

  const handlePeriodChange = (day: string, value: "both" | "morning" | "afternoon") => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], openPeriod: value },
    }));
    setScheduleToParent(schedule);
  };

  const handleDateChange = (type: "start" | "end", value: Date | null) => {
    if (type === "start") {
      setVacationStart(value);
    } else {
      setVacationEnd(value);
    }
  };

  const submit = async () => {
    // Validate all time ranges before submitting
    for (const [day, daySchedule] of Object.entries(schedule)) {
      if (daySchedule.isOpen) {
        if (
          daySchedule.openPeriod !== "afternoon" &&
          daySchedule.morningOpen > daySchedule.morningClose
        ) {
          alert(`Invalid morning time range for ${day}`);
          return;
        }
        if (
          daySchedule.openPeriod !== "morning" &&
          daySchedule.afternoonOpen > daySchedule.afternoonClose
        ) {
          alert(`Invalid afternoon time range for ${day}`);
          return;
        }
      }
    }
  };

  return {
    schedule,
    vacationStart,
    vacationEnd,
    handleDayToggle,
    handleContinuousToggle,
    handleTimeChange,
    handlePeriodChange,
    handleDateChange,
    submit,
    parseISO,
    format,
  };
};
